export type ScopeType = "Group" | "Global" | "Application";
export interface ScopeObject{
  type: ScopeType,
  objectId: string
}
export interface RoleObject{
  scope: ScopeObject,
  roleId:  string
}
export interface Group{
  id: string,
  displayName: string,
  description: string,
  owners: Array<{id: string, type: number}>,
  adminWritable: boolean,
  customMetadata: any,
  applications: Array<any>,
}
export interface UserGroupReference {
    id: string,
    name: string,
    customMetadata: any
}

export const getReferenceForGroup: (group: Group) => UserGroupReference = (group: Group) => {
    if (!group) {
        return group;
    }
    return {
        id: group.id,
        name: group.displayName,
        customMetadata: {...group.customMetadata}
    }
}
export interface AdminContextInterface {
    id: string,
    adminApplications: AdminApps,
    allApplications: Record<string, ApplicationReference>,
    allGroups: Record<string, Group>,
    generalPermissions: PermissionsList,
    isSuperAdmin: boolean,
    isOrganizationAdmin: boolean,
    organizationId: string,
    roleSpecificPermissions: Record<string, RoleDefinition>,
    multiOrg: boolean,
    organizationList: Array<{id: string, name: string, apps: Array<string>, protocol: string, isScimOrg: boolean}>
    roles: Array<RoleObject>,
}

export const adminCanViewSomeGroups = (adminCredentials: AdminContextInterface) => {
  if (adminCredentials?.isSuperAdmin) return true;
  const rolePermissions = adminCredentials.roleSpecificPermissions;
  return rolePermissions && adminCredentials.roles.some(role => rolePermissions[role.roleId]?.permissions && Object.keys(rolePermissions[role.roleId]?.permissions ?? {}).some(permission => rolePermissions[role.roleId]?.permissions[permission] && ['submitMembers', 'writeMembers', 'writeOwners', 'readMembers'].includes(permission)));
}

export const adminCanEditSomeGroups = (adminCredentials: AdminContextInterface) => {
  if (adminCredentials?.isSuperAdmin) return true;
  const rolePermissions = adminCredentials.roleSpecificPermissions;
  return rolePermissions && adminCredentials.roles.some(role => rolePermissions[role.roleId]?.permissions && Object.keys(rolePermissions[role.roleId]?.permissions ?? {}).some(permission => rolePermissions[role.roleId]?.permissions[permission] && ['submitMembers', 'writeMembers', 'writeOwners'].includes(permission)));
}

export const adminCanEditUserGroup = (adminCredentials: AdminContextInterface, groupId: string) => {
  const group = adminCredentials?.allGroups?.[groupId]
  if ((!group) || (!group.adminWritable)) {
      return false;
  } else if (adminCredentials.isSuperAdmin) {
      return true;
  }
  const rolePermissions = adminCredentials.roleSpecificPermissions;
  const groupEditRoles = rolesWithPermission(rolePermissions, adminCredentials.roles, "writeMembers");
  const groupsWrite = scopeIdsIncludedByRoles("Group", [groupId], groupEditRoles);
  return groupsWrite && groupsWrite.includes(groupId);
}

export interface RoleDefinitions {
  id: string,
  roles: Record<string, RoleDefinition>
}

export interface RoleDefinition {
  permissions: PermissionsList,
  validScopeTypes: Array<string>,
  isAssignable: boolean,
  requiredToAssign: Array<string>
}

export const assignableAdminRoles = (rolePermissions: Record<string, RoleDefinition>, employeeRoles: Array<RoleObject>, isSuperAdmin: boolean) => {
  return Object.keys(rolePermissions).filter(roleDefinitionId => {
      let roleDefinition = rolePermissions[roleDefinitionId];
      return roleDefinition.isAssignable && (isSuperAdmin || (!roleDefinition.requiredToAssign) || employeeRoles.some(eRole => Object.keys(rolePermissions[eRole.roleId]?.permissions ?? {}).some(permission => (rolePermissions[eRole.roleId].permissions[permission] && roleDefinition.requiredToAssign.includes(permission)))));
  })
}

export const assignableRoleScopeTypes = (scopeTypes: Array<string>, rolePermissions: Record<string, RoleDefinition>, roles: Array<RoleObject>, forRole: string, isSuperAdmin: boolean) => {
  if (isSuperAdmin) {
      return scopeTypes;
  } else {
      return scopeTypesIncludedByRoles(scopeTypes, rolesThatCanAssignTargetRole(
          rolePermissions,
          roles,
          forRole
      ));
  }
}

export const assignableRoleScopeIds = (scopeType: string, scopeIds: Array<string>, rolePermissions: Record<string, RoleDefinition>, roles: Array<RoleObject>, forRole: string, isSuperAdmin: boolean) => {
  if (isSuperAdmin) {
      return scopeIds;
  } else {
      return scopeIdsIncludedByRoles(
          scopeType,
          scopeIds,
          rolesThatCanAssignTargetRole(rolePermissions, roles, forRole)
      );
  }
}

export const adminCanRemoveRole = (rolePermissions: Record<string, RoleDefinition>, adminRoles: Array<RoleObject>, targetRole: RoleObject, isSuperAdmin: boolean) => {
  if (isSuperAdmin) {
      return true;
  }
  let usableRoles = rolesThatCanAssignTargetRole(rolePermissions, adminRoles, targetRole.roleId).filter(role => role.scope.type == targetRole.scope.type || role.scope.type == "Global");
  if (targetRole.scope.type == "Global") {
      return usableRoles.some(uRole => (uRole.scope.type === "Global"));
  } else {
      return usableRoles.some(uRole => (uRole.scope.type === "Global") || (uRole.scope.objectId === targetRole.scope.objectId));
  }
}

export const rolesWithPermission = (rolePermissions: Record<string, RoleDefinition>, roles: Array<RoleObject>, permission: string) => {
  return roles.filter(role => Object.keys(rolePermissions?.[role.roleId]?.permissions ?? {}).includes(permission) && rolePermissions[role.roleId].permissions[permission]);
}

export const rolesWithPermissionFromSet = (rolePermissions: Record<string, RoleDefinition>, roles: Array<RoleObject>, permissions: Array<string>) => {
  return roles.filter(role => permissions.some(permission => Object.keys(rolePermissions?.[role.roleId]?.permissions ?? {}).includes(permission) && rolePermissions[role.roleId].permissions[permission]));
}

export const rolesThatCanAssignTargetRole = (rolePermissions: Record<string, RoleDefinition>, roles: Array<RoleObject>, targetRole: string) => {
  if (!(rolePermissions?.[targetRole])) {
      return [];
  }
  if (!rolePermissions[targetRole].requiredToAssign) {
      return roles;
  } else {
      return rolesWithPermissionFromSet(rolePermissions, roles, rolePermissions[targetRole].requiredToAssign);
  }
}

export const scopeTypesIncludedByRoles = (scopeTypes: Array<string>, roles: Array<RoleObject>) => {
  if (roles.some(role => role.scope.type === "Global")) {
      return scopeTypes;
  } else {
      return scopeTypes.filter(scopeType => roles.some(role => role.scope.type === scopeType));
  }
}

export const scopeIdsIncludedByRoles = (scopeType: string, scopeIds: Array<string>, roles: Array<RoleObject>) => {
  if (roles.some(role => role.scope.type === "Global")) {
      return scopeIds;
  } else {
      let scopedRoles = roles?.filter(role => role.scope.type == scopeType) ?? [];
      return scopeIds?.filter(scopeId => scopedRoles.some(scopedRole => scopedRole.scope.objectId == scopeId)) ?? [];
  }
}

export type PermissionsList = Record<string, boolean>;

export interface ApplicationReference {
    id: string,
    name: string,
    isGlobal: boolean
}

export const rolePermission = ( permission: string, role: string, roleSpecificPermissions: Record<string, RoleDefinition>, isSuperAdmin: boolean): boolean => {
    if(isSuperAdmin) return true;
    if(roleSpecificPermissions[role] === undefined || roleSpecificPermissions[role]?.permissions?.[permission as keyof PermissionsList] === undefined) return false;
    return roleSpecificPermissions[role]?.permissions?.[permission as keyof PermissionsList] === true;
}

export type AdminApps = Array<{ id: string, name: string, role: string, isGlobal: boolean }>

export interface OptionsType {
    value: string,
    label: string,
    isDisabled: boolean,
    hidden: boolean
}

export interface BasicIDToken {
    aud: string,
    oid: string,
    preferred_username: string,
    [key: string]: any
}

export interface ApplicationRoles {
    [key: string]: string
}

export interface PasswordCredential {
  displayName: string,
  endDateTime: string,
  hint: string,
  keyId: string,
  startDateTime: string
}

export interface Admin {
    id: string,
    securityGroups: Array<any>,
    applicationSpecificRoles: ApplicationRoles,
    organizationId: string,
    isSuperAdmin: boolean,
    isOrganizationAdmin: boolean,
    isActive: boolean,
    administeredOrganizations: string[] | undefined,
    roles: Array<RoleObject>
}

export interface Scope {
  id: string;
  displayName: string;
  value: string;
  description?: string;
}

export interface ScopeGrant {
  principalId: string;
  scopes: Scope[];
  grantId?: string;
}

export interface GroupMap{
  groupName: string;
  clientGroupId: string;
}

export interface RESTScimAppConfig {
  name?: string;
  id: string;
  appId: string;
  orgId?: string;
  tenantId?: string;
  // claims: ClaimObj[],
  // requiresAdminApproval: boolean;
  // applicationHomePageUrl: string;
  // redirectUris: RedirectURIs;
  // logoutURL: string;
  // implicitGrant: boolean;
  // signInTrigger: ISignInTrigger;
  // iconURL: string;
  // sendWelcomeEmail: boolean;
  isActive: boolean;
  createUserTrigger: any
  updateUserTrigger: any;
  deactivateUserTrigger: any;
  reactivateUserTrigger: any;
  deleteUserTrigger: any;
  // fromEmailAddress: string;
  // emailTemplates: EmailTemplate[];
  // identifierUris: string[];
  // oauth2Scopes: ScopeGrant[];
  // appRoles: Scope[];
  // requiredResourceAccess: string[];
  // supportRequestEmail: string;
  // selfServiceSignUp: SelfServiceSignUpSettings;
  // pendingEnrollmentNotifications: PendingEnrollmentNotificationsSettings;
  // suppressRoleAndApplicationClaims: boolean;
  // loginAutoAssignedGroups: Array<string>;
  // isGlobal: boolean;
  secrets: Array<PasswordCredential>;
  groupMapping: Record<string, GroupMap>;
  emailNotification: boolean;
}